import React, { useState, useEffect, useMemo, useCallback } from 'react'
import axios from 'axios'
import { usePlayback } from '../../component/workspaceReplay2/hooks/usePlayback'
import ReplayControls from '../../component/workspaceReplay2/components/ReplayControls'
import { taskContent } from '../content/tasks'
import { useStudyReplayState } from './useStudyReplayState'
import StudyReplayIDE from './StudyReplayIDE'
import StudyEventLog from './StudyEventLog'
import './study-replay.scss'

const BASE = process.env.REACT_APP_HOST_API + '/study'
const KEY_STORE = 'study_admin_key'

// The replay endpoints hand out every participant's invite token and full session, so
// outside test mode they require STUDY_ADMIN_KEY. Held locally, sent as a header.
const adminHeaders = () => {
  const k = localStorage.getItem(KEY_STORE)
  return k ? { 'x-study-admin-key': k } : {}
}

const fmtDur = (ms) => {
  if (!ms) return '—'
  const m = Math.floor(ms / 60000)
  const s = Math.floor(ms / 1000) % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "Aug 11, 6:48 PM" — and "today"/"yesterday" for the recent ones, which is how you
// actually think about a run you did an hour ago.
const fmtWhen = (ms) => {
  const d = new Date(ms)
  const day = new Date(ms).setHours(0, 0, 0, 0)
  const today = new Date().setHours(0, 0, 0, 0)
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const days = Math.round((today - day) / 864e5)
  if (days === 0) return `today ${time}`
  if (days === 1) return `yesterday ${time}`
  return `${MONTH[d.getMonth()]} ${d.getDate()}, ${time}`
}

// Booked follow-up, with how far out it is — "in 3 days" is what you actually scan for
// when working out who to remind, and "overdue" is what you scan for when chasing.
const fmtBooking = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return null
  const days = Math.round((d.setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 864e5)
  const when = new Date(iso)
  const date = `${MONTH[when.getMonth()]} ${when.getDate()}`
  const time = when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  const rel = days === 0 ? 'today' : days === 1 ? 'tomorrow' : days > 0 ? `in ${days}d` : `${-days}d overdue`
  return { date, time, rel, overdue: days < 0, soon: days >= 0 && days <= 1 }
}

// A step can be attempted more than once — a participant who reloads, and a researcher
// walking the flow repeatedly, both produce several visits to the same stepId. Treating
// them as one slice made learn1 span seven days with eight specks of activity in it, and
// a timeline that long is unscrubbable. Split into runs.
//
// SESSION_START is the boundary rather than a gap heuristic: StudyTelemetryProvider emits
// it on mount, so one event is one visit to the screen. The gap rule is only a fallback
// for streams recorded before that (or where the first flush was lost).
const RUN_GAP_MS = 10 * 60 * 1000

function splitRuns (evs) {
  const runs = []
  let cur = null
  for (const e of evs) {
    const boundary = !cur ||
      e.type === 'SESSION_START' ||
      (e.timestamp - cur[cur.length - 1].timestamp) > RUN_GAP_MS
    if (boundary) { cur = []; runs.push(cur) }
    cur.push(e)
  }
  return runs
}

const summarise = (evs) => ({
  events: evs,
  startTime: evs[0].timestamp,
  duration: evs[evs.length - 1].timestamp - evs[0].timestamp,
  eventCount: evs.length,
  runCount: evs.filter(e => e.type === 'TERMINAL_RUN').length,
  queries: evs.filter(e => e.type === 'CHAT_QUERY').length,
  outcome: evs.find(e => e.type === 'TASK_COMPLETED') ? 'submitted'
    : evs.find(e => e.type === 'TASK_TIMEOUT') ? 'timed out' : null
})

// Per-step slice of a participant's stream, each step carrying its runs newest-first —
// the most recent attempt is nearly always the one being looked for.
function sliceSteps (participant, events) {
  const byStep = new Map()
  for (const e of events) {
    if (!e.stepId) continue
    if (!byStep.has(e.stepId)) byStep.set(e.stepId, [])
    byStep.get(e.stepId).push(e)
  }
  return participant.steps.map(step => {
    const evs = (byStep.get(step.stepId) || []).sort((a, b) => a.timestamp - b.timestamp)
    const runs = evs.length ? splitRuns(evs).map(summarise).reverse() : []
    return { ...step, runs, eventCount: evs.length }
  })
}

export default function StudyReplayPage () {
  const [participants, setParticipants] = useState([])
  const [selectedPid, setSelectedPid] = useState(null)
  const [stream, setStream] = useState(null)      // { participantId, source, events }
  const [selectedStepId, setSelectedStepId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [showLog, setShowLog] = useState(true)

  const [needsKey, setNeedsKey] = useState(false)
  const [keyInput, setKeyInput] = useState('')

  const loadParticipants = useCallback(() => {
    setError('')
    axios.get(`${BASE}/replay/participants`, { headers: adminHeaders() })
      .then(r => { setParticipants(r.data); setNeedsKey(false) })
      .catch(e => {
        const status = e.response?.status
        if (status === 403 || status === 503) setNeedsKey(true)
        setError(e.response?.data?.error || e.message)
      })
  }, [])

  useEffect(() => { loadParticipants() }, [loadParticipants])

  const openParticipant = useCallback(async (pid) => {
    setSelectedPid(pid); setStream(null); setSelectedStepId(null); setLoading(true); setError('')
    try {
      const r = await axios.get(`${BASE}/replay/events`, {
        params: { participantId: pid },
        headers: adminHeaders()
      })
      setStream(r.data)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setLoading(false) }
  }, [])

  const setEligibility = useCallback(async (pid, verdict) => {
    setError('')
    try {
      await axios.post(`${BASE}/admin/eligibility`,
        { participantId: pid, eligibility: verdict },
        { headers: adminHeaders() })
      loadParticipants()
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    }
  }, [loadParticipants])

  const participant = participants.find(p => p.participantId === selectedPid) || null

  const steps = useMemo(
    () => (participant && stream) ? sliceSteps(participant, stream.events) : [],
    [participant, stream]
  )

  // Selection is a run, not a step — "learn1" alone is ambiguous once it's been
  // attempted more than once.
  const [stepId, runIdx] = (selectedStepId || '#').split('#')
  const step = steps.find(s => s.stepId === stepId) || null
  const run = step?.runs?.[Number(runIdx)] || null

  // Auto-select the most recent run that has telemetry.
  useEffect(() => {
    if (!steps.length || selectedStepId) return
    const first = steps.find(s => s.runs.length > 0)
    if (first) setSelectedStepId(`${first.stepId}#0`)
  }, [steps, selectedStepId])

  const task = step?.task ? taskContent(step.task) : null
  const duration = run?.duration || 0
  const playback = usePlayback(duration)

  // Rewind whenever the selected run changes.
  const resetRef = playback.reset
  useEffect(() => { resetRef() }, [selectedStepId, resetRef])

  const state = useStudyReplayState(
    run?.events || [], playback.currentTime, run?.startTime || 0, task?.starter || ''
  )

  const remaining = step?.timedSec != null
    ? Math.max(0, step.timedSec - Math.floor(playback.currentTime / 1000))
    : null

  return (
    <div className="srp">
      {/* ── Sidebar ────────────────────────────────────────────────────────── */}
      <aside className="srp-side">
        <div className="srp-side__head">
          <h1>Study Replay</h1>
          <p>Reconstructed from collected telemetry</p>
        </div>

        {error && <div className="srp-error">{error}</div>}

        {needsKey && (
          <div className="srp-key">
            <label>Researcher key</label>
            <input
              type="password"
              value={keyInput}
              placeholder="STUDY_ADMIN_KEY"
              onChange={e => setKeyInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && keyInput && (
                localStorage.setItem(KEY_STORE, keyInput), loadParticipants()
              )}
            />
            <button
              className="srp-toggle"
              disabled={!keyInput}
              onClick={() => { localStorage.setItem(KEY_STORE, keyInput); loadParticipants() }}
            >Unlock</button>
          </div>
        )}

        <div className="srp-side__list">
          {!participants.length && !error && <div className="srp-empty">No participants yet.</div>}
          {participants.map(p => {
            const open = p.participantId === selectedPid
            const booking = fmtBooking(p.bookingAt)
            return (
              <div key={p.participantId} className={`srp-p${open ? ' is-open' : ''}`}>
                <button className="srp-p__head" onClick={() => open ? setSelectedPid(null) : openParticipant(p.participantId)}>
                  <span className="srp-p__id">
                    {p.label || p.participantId}
                    {p.label && <span className="srp-p__token">{p.participantId}</span>}
                  </span>
                  <span className="srp-p__meta">
                    cell {p.cell} · {p.phase}
                    {p.completedMain && <span className="srp-tag">main done</span>}
                    {!p.dbSessionId && <span className="srp-tag srp-tag--warn">no db</span>}
                  </span>
                  {/* What this participant is waiting on. Emails are sent by hand, so
                      the row has to say plainly who needs action right now. */}
                  {p.action && p.action.key !== 'idle' && (
                    <span className={`srp-act srp-act--${p.action.key}${p.action.due ? ' is-due' : ''}`}>
                      {p.action.due && <span className="srp-act__dot" />}
                      {p.action.label}
                    </span>
                  )}

                  {/* Recording reference — what you match a screen recording against. */}
                  {p.recording && (
                    <span className="srp-rec" title={`recording ref: ${p.recording.ref}`}>
                      rec {p.recording.ref}
                    </span>
                  )}

                  {/* Follow-up booking: the whole point of the day 6–8 gate is that
                      someone has to chase these, so it belongs on the collapsed row. */}
                  {booking
                    ? (
                      <span className={`srp-book${booking.overdue ? ' is-overdue' : booking.soon ? ' is-soon' : ''}`}>
                        probe {booking.date}, {booking.time}
                        <span className="srp-book__rel">{booking.rel}</span>
                      </span>
                    )
                    : p.completedMain
                      ? <span className="srp-book is-missing">no probe booked</span>
                      : null}
                </button>

                {open && (
                  <div className="srp-p__steps">
                    {/* Eligibility review: scoring the warm-up is what releases part 2,
                        and nothing automatic can do it (see warmup_task.md's gate). */}
                    {p.completedScreening && (
                      <div className="srp-elig">
                        <span className="srp-elig__label">
                          warm-up eligibility
                          {p.eligibility && <b> · {p.eligibility}</b>}
                        </span>
                        <div className="srp-elig__row">
                          <button
                            className={`srp-chip${p.eligibility === 'eligible' ? ' is-on' : ''}`}
                            onClick={() => setEligibility(p.participantId, 'eligible')}
                          >eligible → release part 2</button>
                          <button
                            className={`srp-chip${p.eligibility === 'excluded' ? ' is-on' : ''}`}
                            onClick={() => setEligibility(p.participantId, 'excluded')}
                          >exclude</button>
                        </div>
                      </div>
                    )}

                    {loading && <div className="srp-empty">Loading telemetry…</div>}
                    {!loading && steps.map(s => (
                      <div className="srp-stepgroup" key={s.stepId}>
                        <div className="srp-stepgroup__name">
                          {s.stepId}
                          {s.aiAvailable && <span className="srp-dot srp-dot--ai" title="AI tutor available" />}
                          {s.runs.length > 1 && <span className="srp-stepgroup__n">{s.runs.length} runs</span>}
                        </div>

                        {!s.runs.length && <div className="srp-step is-empty">no telemetry</div>}

                        {/* One row per attempt, newest first, each labelled by when it
                            happened — the only way to tell repeat runs apart. */}
                        {s.runs.map((r, i) => {
                          const key = `${s.stepId}#${i}`
                          return (
                            <button
                              key={key}
                              className={`srp-step${key === selectedStepId ? ' is-on' : ''}`}
                              onClick={() => setSelectedStepId(key)}
                            >
                              <span className="srp-step__when">{fmtWhen(r.startTime)}</span>
                              <span className="srp-step__stats">
                                {fmtDur(r.duration)} · {r.eventCount} ev · {r.runCount} run{r.runCount === 1 ? '' : 's'}
                                {s.aiAvailable ? ` · ${r.queries}q` : ''}
                              </span>
                              {r.outcome && (
                                <span className={`srp-outcome srp-outcome--${r.outcome === 'submitted' ? 'ok' : 'to'}`}>
                                  {r.outcome}
                                </span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    ))}
                    {!loading && stream && (
                      <div className="srp-source">
                        source: <b>{stream.source}</b> · {stream.events.length} events total
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </aside>

      {/* ── Stage ──────────────────────────────────────────────────────────── */}
      <main className="srp-main">
        {!step && (
          <div className="srp-placeholder">
            <p>Select a participant, then a coding step, to replay it.</p>
          </div>
        )}

        {step && run && (
          <>
            <div className="srp-bar">
              <div className="srp-bar__left">
                <b>{selectedPid}</b> · {step.stepId}
                <span className="srp-bar__sub">
                  {step.type === 'transfer' ? 'transfer' : 'learning'} · task {step.task} · {step.phase}
                </span>
              </div>
              <div className="srp-bar__right">
                <span className="srp-stat">{fmtWhen(run.startTime)}</span>
                <span className="srp-stat">{fmtDur(run.duration)}</span>
                <span className="srp-stat">{run.eventCount} events</span>
                <span className="srp-stat">{state.runCount} runs so far</span>
                <button className="srp-toggle" onClick={() => setShowLog(v => !v)}>
                  {showLog ? 'Hide log' : 'Show log'}
                </button>
              </div>
            </div>

            <div className="srp-stage">
              <div className="srp-ide">
                <StudyReplayIDE state={state} task={task} step={step} remaining={remaining} />
              </div>
              {showLog && (
                <StudyEventLog
                  events={run.events}
                  startTime={run.startTime}
                  currentTime={playback.currentTime}
                  onSeek={playback.seek}
                  filter={filter}
                  setFilter={setFilter}
                />
              )}
            </div>

            <ReplayControls
              currentTime={playback.currentTime}
              duration={playback.duration}
              isPlaying={playback.isPlaying}
              playbackSpeed={playback.playbackSpeed}
              onTogglePlay={playback.togglePlay}
              onSeek={playback.seek}
              onReset={playback.reset}
              onCycleSpeed={playback.cycleSpeed}
              events={run.events}
              startTime={run.startTime}
              showEventMarkers
              viewOnly
            />
          </>
        )}
      </main>
    </div>
  )
}
